import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, getCardgames, getProductTypes, updateStoreStock, getMyStore } from '../../services/supabaseService';
import { useNotification } from '../../components/member/NotificationProvider';
import { supabase } from '../../lib/supabase';
import { isProductPreSale } from '../../types';

interface LojistaCatalogViewProps {
  store?: any;
  onSwitchToMarketplace?: () => void;
}

export const LojistaCatalogView: React.FC<LojistaCatalogViewProps> = ({
  store: initialStore,
  onSwitchToMarketplace
}) => {
  const { showNotification } = useNotification();
  const [store, setStore] = useState<any>(initialStore || null);
  const [products, setProducts] = useState<any[]>([]);
  const [stockMap, setStockMap] = useState<Map<number, { quantity: number; price: number | null; pre_sale: boolean }>>(new Map());
  const [games, setGames] = useState<any[]>([]);
  const [productTypes, setProductTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGameId, setSelectedGameId] = useState<number | 'all'>('all');
  const [selectedTypeId, setSelectedTypeId] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'unadded' | 'added'>('unadded');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;

  // Inline edit state per product: { [productId]: { quantity, price, preSale, saving, saved } }
  const [itemStates, setItemStates] = useState<Record<number, {
    quantity: number;
    price: number;
    preSale: boolean;
    saving?: boolean;
    saved?: boolean;
  }>>({});

  // Fetch Store if not provided
  useEffect(() => {
    if (!store) {
      getMyStore().then(s => {
        if (s) setStore(s);
      });
    }
  }, [store]);

  // Fetch Store Stock
  const fetchStoreStock = async (storeId: number | string) => {
    try {
      const { data, error } = await supabase
        .from('store_products')
        .select('product_id, quantity, store_price, pre_sale')
        .eq('store_id', storeId)
        .is('deleted_at', null);

      if (error) throw error;

      const map = new Map<number, { quantity: number; price: number | null; pre_sale: boolean }>();
      (data || []).forEach(item => {
        map.set(item.product_id, {
          quantity: item.quantity,
          price: item.store_price,
          pre_sale: !!item.pre_sale
        });
      });
      setStockMap(map);
    } catch (e) {
      console.error("Error fetching store stock:", e);
    }
  };

  useEffect(() => {
    if (store?.id) {
      fetchStoreStock(store.id);
    }
  }, [store?.id]);

  // Fetch Products & Metadata
  useEffect(() => {
    setLoading(true);
    let isMounted = true;

    const unsub = getProducts((data) => {
      if (!isMounted) return;
      // Filter out tickets/subscriptions
      const validProducts = data.filter(p => {
        const isTicket = p.product_type_id === 52 || p.product_type_id === 3 || p.product_types?.name === 'Ticket' || p.product_types?.name === 'Ingresso' || p.product_types?.name === 'Inscrição';
        return !isTicket;
      });
      setProducts(validProducts);
      setLoading(false);
    });

    const fetchMeta = async () => {
      try {
        const [gameList, typesList] = await Promise.all([
          getCardgames(),
          getProductTypes()
        ]);
        if (isMounted) {
          setGames(gameList || []);
          setProductTypes(typesList || []);
        }
      } catch (e) {
        console.error("Error fetching metadata:", e);
      }
    };

    fetchMeta();

    return () => {
      isMounted = false;
      unsub();
    };
  }, []);

  // Form state helper
  const getFormState = (product: any) => {
    if (itemStates[product.id]) {
      return itemStates[product.id];
    }
    const inStock = stockMap.get(product.id);
    const rawVal = inStock?.price !== null && inStock?.price !== undefined
      ? Number(inStock.price)
      : Number(product.msrp || product.mspr || 0.05);
    const defaultPrice = Math.max(0.05, Math.round(rawVal * 100) / 100);

    return {
      quantity: inStock?.quantity ?? 1,
      price: defaultPrice,
      preSale: inStock?.pre_sale ?? false,
      saving: false,
      saved: false
    };
  };

  const handleFieldChange = (productId: number, field: 'quantity' | 'price' | 'preSale', value: any) => {
    setItemStates(prev => {
      const current = prev[productId] || getFormState(products.find(p => p.id === productId));
      return {
        ...prev,
        [productId]: {
          ...current,
          [field]: value,
          saved: false
        }
      };
    });
  };

  const handleQuickAdd = async (product: any) => {
    if (!store?.id) {
      showNotification("Loja não encontrada para sua conta.", "error");
      return;
    }

    const state = getFormState(product);
    const quantity = Math.max(1, Number(state.quantity) || 1);
    const price = Number(state.price);

    if (isNaN(price) || price < 0.05) {
      showNotification("O valor mínimo de um produto no estoque é de R$ 0,05 (5 centavos).", "error");
      return;
    }

    setItemStates(prev => ({
      ...prev,
      [product.id]: {
        ...state,
        saving: true
      }
    }));

    try {
      const finalPrice = Math.max(0.05, price);
      const res = await updateStoreStock(store.id, product.id, quantity, finalPrice, state.preSale);

      if (!res || (res as any).error) {
        throw new Error((res as any)?.error || "Erro ao atualizar estoque.");
      }

      // Update local stockMap
      setStockMap(prev => {
        const next = new Map(prev);
        next.set(product.id, {
          quantity,
          price: finalPrice,
          pre_sale: state.preSale
        });
        return next;
      });

      setItemStates(prev => ({
        ...prev,
        [product.id]: {
          ...state,
          quantity,
          price: finalPrice,
          saving: false,
          saved: true
        }
      }));

      showNotification(`${product.beauty_name || product.name} adicionado ao seu estoque!`, "success");

      setTimeout(() => {
        setItemStates(prev => {
          if (!prev[product.id]) return prev;
          return {
            ...prev,
            [product.id]: {
              ...prev[product.id],
              saved: false
            }
          };
        });
      }, 3000);
    } catch (err: any) {
      setItemStates(prev => ({
        ...prev,
        [product.id]: {
          ...state,
          saving: false
        }
      }));
      showNotification(err.message || "Erro ao adicionar produto.", "error");
    }
  };

  // Filtered product types dynamically based on selected game
  const availableProductTypes = useMemo(() => {
    const nonTicket = productTypes.filter((t: any) => {
      const isTicket = 
        Number(t.category_id) === 3 || 
        Number(t.id) === 52 || 
        Number(t.id) === 3 ||
        t.name?.toLowerCase() === 'ticket' || 
        t.name?.toLowerCase() === 'ingresso' || 
        t.name?.toLowerCase() === 'inscrição' ||
        t.name?.toLowerCase() === 'inscricao';
      return !isTicket;
    });

    if (selectedGameId === 'all') return nonTicket;
    const targetGameId = Number(selectedGameId);
    return nonTicket.filter((t: any) => t.game_id === null || Number(t.game_id) === targetGameId);
  }, [productTypes, selectedGameId]);

  // Reset selectedTypeId if no longer available in selected game
  useEffect(() => {
    if (selectedTypeId !== 'all') {
      const isStillAvailable = availableProductTypes.some((t: any) => Number(t.id) === Number(selectedTypeId));
      if (!isStillAvailable) {
        setSelectedTypeId('all');
      }
    }
  }, [selectedGameId, availableProductTypes, selectedTypeId]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const search = searchTerm.trim().toLowerCase();
      if (search) {
        const name = (product.name || '').toLowerCase();
        const beautyName = (product.beauty_name || '').toLowerCase();
        const typeName = (product.product_type_name || product.product_types?.name || '').toLowerCase();
        const gameName = (product.game_name || product.games?.name || '').toLowerCase();
        if (!name.includes(search) && !beautyName.includes(search) && !typeName.includes(search) && !gameName.includes(search)) {
          return false;
        }
      }

      if (selectedGameId !== 'all') {
        const gameId = product.game_id || product.games?.id;
        if (Number(gameId) !== Number(selectedGameId)) return false;
      }

      if (selectedTypeId !== 'all') {
        const typeId = product.product_type_id || product.product_types?.id;
        if (Number(typeId) !== Number(selectedTypeId)) return false;
      }

      const isInStock = stockMap.has(product.id);
      if (statusFilter === 'unadded' && isInStock) return false;
      if (statusFilter === 'added' && !isInStock) return false;

      return true;
    });
  }, [products, searchTerm, selectedGameId, selectedTypeId, statusFilter, stockMap]);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGameId, selectedTypeId, statusFilter]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-24 px-4 sm:px-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/60 border border-slate-800 p-6 rounded-3xl backdrop-blur-sm">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
              Painel do Lojista
            </span>
            <span className="text-xs text-slate-500">• Catálogo Geral Cardumy</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white italic tracking-tight uppercase">
            Adicionar Produtos ao Estoque
          </h1>
          <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-2xl">
            Navegue pelo catálogo oficial de selados, caixas e acessórios. Defina o seu preço de venda e quantidade para abastecer sua loja com um clique.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          {onSwitchToMarketplace && (
            <button
              onClick={onSwitchToMarketplace}
              className="px-4 py-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/60 text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2"
              title="Alternar para a visualização pública do marketplace como cliente"
            >
              <i className="fas fa-eye text-slate-400"></i> Modo Cliente
            </button>
          )}

          <Link
            to="/gerenciar-estoque"
            className="px-5 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider shadow-lg shadow-purple-600/20 hover:shadow-purple-500/30 transition-all flex items-center gap-2"
          >
            <i className="fas fa-boxes-stacked"></i>
            <span>Ver Meu Estoque ({stockMap.size})</span>
          </Link>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-slate-900/40 border border-slate-800 p-4 sm:p-5 rounded-2xl space-y-4 shadow-xl">
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
          {/* Search */}
          <div className="sm:col-span-6 relative">
            <i className="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs"></i>
            <input
              type="text"
              placeholder="Buscar por nome do produto, booster box, deck, sleeve..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-9 pr-9 py-2.5 text-xs text-white placeholder:text-slate-500 font-medium focus:outline-none focus:border-purple-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
              >
                <i className="fas fa-times-circle"></i>
              </button>
            )}
          </div>

          {/* Game Select */}
          <div className="sm:col-span-3">
            <select
              value={selectedGameId}
              onChange={(e) => setSelectedGameId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-300 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer"
            >
              <option value="all">Todos os Jogos</option>
              {games.map(g => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          {/* Type Select */}
          <div className="sm:col-span-3">
            <select
              value={selectedTypeId}
              onChange={(e) => setSelectedTypeId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-300 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer"
            >
              <option value="all">Todas as Subcategorias</option>
              {availableProductTypes.map((t: any) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-800/60 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStatusFilter('unadded')}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                statusFilter === 'unadded'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
              }`}
            >
              Fora do Estoque ({products.filter(p => !stockMap.has(p.id)).length})
            </button>
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                statusFilter === 'all'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
              }`}
            >
              Todos ({products.length})
            </button>
            <button
              onClick={() => setStatusFilter('added')}
              className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer ${
                statusFilter === 'added'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/20'
                  : 'bg-slate-800/60 text-slate-400 hover:text-slate-200'
              }`}
            >
              No Meu Estoque ({stockMap.size})
            </button>
          </div>

          <span className="text-xs text-slate-500 font-medium">
            Encontrados: <strong className="text-slate-300">{filteredProducts.length}</strong> itens
          </span>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-28 space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Carregando catálogo Cardumy...</p>
        </div>
      ) : paginatedProducts.length === 0 ? (
        <div className="bg-slate-900/30 border border-slate-800 p-16 rounded-3xl text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/60 border border-slate-700/50 flex items-center justify-center mx-auto text-slate-600 text-3xl">
            <i className="fas fa-box-open"></i>
          </div>
          <h3 className="text-base font-bold text-slate-300">Nenhum produto encontrado</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto">
            {statusFilter === 'unadded'
              ? 'Todos os produtos encontrados com os filtros atuais já foram adicionados ao seu estoque!'
              : 'Tente limpar ou ajustar os filtros de busca para encontrar outros produtos.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
          {paginatedProducts.map(product => {
            const inStock = stockMap.get(product.id);
            const isInStock = !!inStock;
            const state = getFormState(product);
            const msrp = product.msrp || product.mspr || 0;

            return (
              <div
                key={product.id}
                className={`flex flex-col justify-between rounded-2xl border p-4 transition-all duration-200 ${
                  isInStock
                    ? 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                    : 'bg-slate-900/80 border-slate-800 hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-950/20'
                }`}
              >
                <div>
                  {/* Top Badges & Status */}
                  <div className="flex items-center justify-between gap-1.5 mb-2.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {isProductPreSale(product) && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase tracking-wider flex items-center gap-1 shadow-xs">
                          <i className="fas fa-clock text-[8px] text-amber-400"></i>
                          <span>Pré-Venda</span>
                        </span>
                      )}
                      {product.game_name && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400 border border-purple-500/20 uppercase tracking-wider">
                          {product.game_name}
                        </span>
                      )}
                      {product.product_type_name && (
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-md bg-slate-800 text-slate-400 uppercase tracking-wider">
                          {product.product_type_name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Image */}
                  <div className="h-40 w-full rounded-xl bg-slate-950 border border-slate-800/80 p-2 flex items-center justify-center overflow-hidden mb-3 relative">
                    {isProductPreSale(product) && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-amber-500/25 border border-amber-500/50 text-[8px] font-black uppercase tracking-wider text-amber-300 flex items-center gap-1 backdrop-blur-xs">
                        <i className="fas fa-clock text-[7px] text-amber-400"></i>
                        <span>Pré-Venda</span>
                      </div>
                    )}
                    <img
                      src={product.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=300'}
                      alt={product.name}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                      onError={(e: any) => {
                        e.currentTarget.src = 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=300';
                      }}
                    />
                  </div>

                  {/* Title & Info */}
                  <div className="space-y-1 mb-3">
                    <h3 className="font-bold text-sm text-white line-clamp-2 leading-tight" title={product.beauty_name || product.name}>
                      {product.beauty_name || product.name}
                    </h3>

                    {product.release_date && isProductPreSale(product) && (
                      <p className="text-[10px] text-amber-400 font-bold flex items-center gap-1 pt-0.5">
                        <i className="fas fa-calendar-alt text-[8.5px]"></i>
                        <span>Lançamento: {new Date(product.release_date).toLocaleDateString('pt-BR')}</span>
                      </p>
                    )}

                    {msrp > 0 && (
                      <p className="text-[11px] text-slate-500">
                        Sugerido (MSRP): <span className="font-bold text-slate-300">R$ {Number(msrp).toFixed(2).replace('.', ',')}</span>
                      </p>
                    )}

                    {isInStock ? (
                      <div className="pt-1">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-black px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                          <i className="fas fa-check text-[8px]"></i>
                          No estoque: {inStock.quantity} un. • R$ {Number(inStock.price || 0).toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                    ) : (
                      <div className="pt-1">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-lg bg-slate-800 text-slate-400">
                          <i className="fas fa-circle text-[6px] text-slate-600"></i>
                          Fora do estoque
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Inline Add / Edit Form */}
                <div className="mt-2 pt-3 border-t border-slate-800/80 space-y-2.5">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Quantity Stepper */}
                    <div>
                      <label className="text-[9px] font-black uppercase text-slate-500 block mb-1">Quantidade</label>
                      <div className="flex items-center bg-slate-950 border border-slate-800 rounded-xl overflow-hidden h-[38px]">
                        <button
                          type="button"
                          onClick={() => handleFieldChange(product.id, 'quantity', Math.max(1, (Number(state.quantity) || 1) - 1))}
                          className="w-7 h-full text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors flex items-center justify-center text-xs"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={state.quantity}
                          onChange={(e) => handleFieldChange(product.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                          className="w-full bg-transparent text-center text-xs font-bold text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleFieldChange(product.id, 'quantity', (Number(state.quantity) || 1) + 1)}
                          className="w-7 h-full text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors flex items-center justify-center text-xs"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Price Input (min R$ 0.05) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[9px] font-black uppercase text-slate-500 block">Preço (R$)</label>
                        <span className="text-[8px] text-slate-500">Mín. 0,05</span>
                      </div>
                      <div className={`flex items-center bg-slate-950 border rounded-xl px-2.5 h-[38px] transition-colors ${
                        state.price < 0.05 ? 'border-amber-500/50 bg-amber-950/20' : 'border-slate-800'
                      }`}>
                        <span className="text-slate-600 text-xs mr-1 font-bold">R$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0.05"
                          value={state.price}
                          onChange={(e) => handleFieldChange(product.id, 'price', parseFloat(e.target.value) || 0)}
                          className="w-full bg-transparent text-xs font-bold text-white focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pre-sale Toggle and Submit Button */}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1.5 cursor-pointer bg-slate-950 border border-slate-800/80 px-2.5 py-2 rounded-xl text-[10px] font-bold text-slate-400 hover:border-slate-700 transition-colors">
                      <input
                        type="checkbox"
                        checked={state.preSale}
                        onChange={(e) => handleFieldChange(product.id, 'preSale', e.target.checked)}
                        className="accent-purple-600 w-3.5 h-3.5 cursor-pointer"
                      />
                      <span>Pré-venda</span>
                    </label>

                    <button
                      type="button"
                      disabled={state.saving}
                      onClick={() => handleQuickAdd(product)}
                      className={`flex-1 h-[38px] px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 whitespace-nowrap ${
                        state.saved
                          ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30'
                          : isInStock
                            ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700 hover:border-purple-500'
                            : 'bg-purple-600 hover:bg-purple-500 text-white shadow-md shadow-purple-600/20 hover:shadow-purple-500/30'
                      }`}
                    >
                      {state.saving ? (
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : state.saved ? (
                        <>
                          <i className="fas fa-check text-[11px]"></i> Salvo!
                        </>
                      ) : isInStock ? (
                        <>
                          <i className="fas fa-arrows-rotate text-[10px]"></i> Atualizar
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus text-[10px]"></i> Ao Estoque
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-6">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            className="px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 disabled:opacity-40 hover:bg-slate-800 transition-colors"
          >
            Anterior
          </button>
          <span className="text-xs text-slate-500 font-bold px-3">
            Página {currentPage} de {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            className="px-4 py-2 rounded-xl bg-slate-900 border border-slate-800 text-xs font-bold text-slate-300 disabled:opacity-40 hover:bg-slate-800 transition-colors"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
};
